// src/components/RepManageTab.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Heading,
  Input,
  InputGroup,
  InputLeftAddon,
  Select,
  Spinner,
  Stack,
  Switch,
  Tag,
  TagLabel,
  Text,
  useToast,
  VStack,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from "@chakra-ui/react";
import { useCognito } from "../utils/useCognito";
import { CheckCircleIcon } from "@chakra-ui/icons";

const API_BASE =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api";

// ---------- utils ----------
const nowIso = () => new Date().toISOString();
const upper = (s) => (s || "").toUpperCase();
const onlyLetters = (s) => (s || "").replace(/[^A-Za-z]/g, "");
const toPhone = (s) => (s || "").replace(/[^\d+]/g, "");
const clampPct = (n) =>
  Math.max(0, Math.min(100, Number.isFinite(+n) ? +n : 0));

async function authedJson(method, url, token, body) {
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    // Try to parse JSON when possible so we can read fields (existingSalesRepId, etc.)
    let parsed;
    try {
      parsed = JSON.parse(t);
    } catch {
      parsed = { message: t || `HTTP ${res.status} ${res.statusText}` };
    }
    const err = new Error(parsed?.message || `HTTP ${res.status} ${res.statusText}`);
    err.status = res.status;
    err.payload = parsed;
    throw err;
  }
  return res.json().catch(() => ({}));
}

async function authedGet(url, token) {
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    const err = new Error(`HTTP ${res.status} ${res.statusText}: ${t}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

// simple debounce hook
function useDebouncedCallback(cb, delay = 400) {
  const t = useRef();
  return useCallback(
    (...args) => {
      if (t.current) clearTimeout(t.current);
      t.current = setTimeout(() => cb(...args), delay);
    },
    [cb, delay]
  );
}

// ---------- component ----------
const RepManageTab = ({ onRepCreated }) => {
  const toast = useToast();
  const { getIdToken } = useCognito();

  // shared
  const [loading, setLoading] = useState(false);

  // -------- CREATE REP --------
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhone] = useState("");
  const [territory, setTerritory] = useState("");
  const [commissionRate, setCommissionRate] = useState(30);
  const [commissionUnlocked, setCommissionUnlocked] = useState(false);
  const [status, setStatus] = useState("active");
  const [customId, setCustomId] = useState(false);
  const [suffix, setSuffix] = useState("");

  const suggestedSuffix = useMemo(() => {
    const f = upper(onlyLetters(firstName)).trim();
    const l = upper(onlyLetters(lastName)).trim();
    const base = `${f.slice(0, 1)}${l}` || "XREP";
    return base;
  }, [firstName, lastName]);

  const computedRepId = useMemo(() => {
    const sfx = customId
      ? upper(onlyLetters(suffix || suggestedSuffix))
      : suggestedSuffix;
    return `CG-REP-${sfx}`;
  }, [customId, suffix, suggestedSuffix]);

  const canSubmitCreate = useMemo(() => {
    return firstName.trim() && lastName.trim() && email.trim();
  }, [firstName, lastName, email]);

  // -------- ASSIGN REP TO FACILITY --------
  const [reps, setReps] = useState([]); // [{repId, firstName, lastName, ...}]
  const [selectedRepId, setSelectedRepId] = useState("");
  const [facilityQuery, setFacilityQuery] = useState("");
  const [facilityResults, setFacilityResults] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState("");
  const [searchingFacilities, setSearchingFacilities] = useState(false);

  // Modal state for override confirmation
  const {
    isOpen: isOverrideOpen,
    onOpen: onOverrideOpen,
    onClose: onOverrideClose,
  } = useDisclosure();
  const [overrideContext, setOverrideContext] = useState({
    repId: "",
    facilityId: "",
    existingSalesRepId: "",
  });
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);

  // Helper to reload reps and optionally select a specific repId
  const reloadRepsAndSelect = async (repIdToSelect) => {
    try {
      const token = getIdToken();
      const r = await authedGet(`${API_BASE}/reps?limit=500`, token);
      const list = r?.reps || [];
      setReps(list);
      if (repIdToSelect) {
        setSelectedRepId(repIdToSelect);
      } else {
        const firstActive = list.find((x) => x.status === "active") || list[0];
        if (firstActive) setSelectedRepId(firstActive.repId);
      }
    } catch (e) {
      toast({
        title: "Failed to reload reps",
        description: e.message,
        status: "error",
        isClosable: true,
      });
    }
  };

  const onCreateRep = async () => {
    try {
      setLoading(true);
      const token = getIdToken();
      const payload = {
        ...(customId ? { repId: computedRepId } : {}),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        phoneNumber: toPhone(phoneNumber),
        territory: territory.trim(),
        commissionRate: clampPct(commissionRate),
        status: status,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      };
      const resp = await authedJson(
        "POST",
        `${API_BASE}/reps`,
        token,
        payload
      );
      toast({
        title: "Rep created",
        description: resp?.repId ? `ID: ${resp.repId}` : "Saved successfully.",
        status: "success",
        isClosable: true,
      });
      onRepCreated?.(resp);

      // reset fields
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setTerritory("");
      if (!customId) setSuffix("");

      // reload & select
      if (resp?.repId) {
        reloadRepsAndSelect(resp.repId);
      } else {
        reloadRepsAndSelect();
      }
    } catch (e) {
      toast({
        title: "Failed to create rep",
        description: e.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // pre-load reps for dropdown
  useEffect(() => {
    (async () => {
      try {
        const token = getIdToken();
        const r = await authedGet(`${API_BASE}/reps?limit=500`, token);
        const list = r?.reps || [];
        setReps(list);
        const firstActive =
          list.find((x) => x.status === "active") || list[0];
        if (firstActive) setSelectedRepId(firstActive.repId);
      } catch (e) {
        toast({
          title: "Failed to load reps",
          description: e.message,
          status: "error",
          isClosable: true,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doFacilitySearch = useCallback(
    async (term) => {
      if (!term || term.trim().length < 2) {
        setFacilityResults([]);
        return;
      }
      try {
        setSearchingFacilities(true);
        const token = getIdToken();
        const resp = await authedGet(
          `${API_BASE}/facilities?search=${encodeURIComponent(
            term.trim()
          )}&limit=20`,
          token
        );
        setFacilityResults(resp?.items || resp?.facilities || []);
      } catch (e) {
        toast({
          title: "Facility search failed",
          description: e.message,
          status: "error",
          isClosable: true,
        });
      } finally {
        setSearchingFacilities(false);
      }
    },
    [getIdToken, toast]
  );

  const debouncedSearch = useDebouncedCallback(doFacilitySearch, 350);

  const onFacilityInput = (v) => {
    setFacilityQuery(v);
    setSelectedFacilityId("");
    debouncedSearch(v);
  };

  const callAssign = async ({ repId, facilityId, override = false }) => {
    const token = getIdToken();
    return authedJson(
      "POST",
      `${API_BASE}/assign-rep-to-facility`,
      token,
      { repId, facilityId, override }
    );
  };

  const onAssign = async () => {
    if (!selectedRepId || !selectedFacilityId) {
      toast({
        title: "Select both rep and facility",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    try {
      setLoading(true);
      await callAssign({
        repId: selectedRepId,
        facilityId: selectedFacilityId,
        override: false,
      });
      toast({
        title: "Assigned rep to facility",
        status: "success",
        isClosable: true,
      });
    } catch (e) {
      // Handle 409 (already assigned) -> open override modal
      if (e?.status === 409) {
        const existing = e?.payload?.existingSalesRepId || "";
        setOverrideContext({
          repId: selectedRepId,
          facilityId: selectedFacilityId,
          existingSalesRepId: existing,
        });
        onOverrideOpen();
      } else {
        toast({
          title: "Failed to assign rep",
          description: e.message,
          status: "error",
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onConfirmOverride = async () => {
    try {
      setOverrideSubmitting(true);
      await callAssign({
        repId: overrideContext.repId,
        facilityId: overrideContext.facilityId,
        override: true,
      });
      toast({
        title: "Rep assignment overridden",
        description: `Facility ${overrideContext.facilityId} is now assigned to ${overrideContext.repId}`,
        status: "success",
        isClosable: true,
      });
      onOverrideClose();
    } catch (e) {
      toast({
        title: "Failed to override assignment",
        description: e.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setOverrideSubmitting(false);
    }
  };

  return (
    <>
      <Stack spacing={8}>
        {/* CREATE REP */}
        <Box borderWidth="1px" borderRadius="lg" p={5} bg="gray.50">
          <HStack justify="space-between" mb={4} wrap="wrap">
            <Heading size="md">Create Sales Rep</Heading>
            <Tag colorScheme="gray">
              <TagLabel>Reps Table</TagLabel>
            </Tag>
          </HStack>

          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>First Name</FormLabel>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  _hover={{ transform: "scale(1)" }}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel>Last Name</FormLabel>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  _hover={{ transform: "scale(1)" }}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  _hover={{ transform: "scale(1)" }}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Phone</FormLabel>
                <InputGroup>
                  <InputLeftAddon>📞</InputLeftAddon>
                  <Input
                    value={phoneNumber}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 555 555 5555"
                    _hover={{ transform: "scale(1)" }}
                  />
                </InputGroup>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Territory</FormLabel>
                <Input
                  value={territory}
                  onChange={(e) => setTerritory(e.target.value)}
                  placeholder="e.g., Southeast"
                  _hover={{ transform: "scale(1)" }}
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Commission Rate (%)</FormLabel>
                <Input
                  type="number"
                  value={commissionRate}
                  onChange={(e) => setCommissionRate(e.target.value)}
                  isDisabled={!commissionUnlocked}
                  _hover={{ transform: "scale(1)" }}
                />
                <FormHelperText>Percent of invoice owed to rep.</FormHelperText>
                <HStack mt={2}>
                  <Switch
                    id="commission-unlock"
                    isChecked={commissionUnlocked}
                    onChange={(e) => setCommissionUnlocked(e.target.checked)}
                    colorScheme="blue"
                  />
                  <Text fontSize="sm" color="gray.600">
                    {commissionUnlocked
                      ? "Manual commission entry enabled"
                      : "Commission locked at 30%"}
                  </Text>
                </HStack>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  _hover={{ transform: "scale(1)" }}
                >
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                </Select>
              </FormControl>
            </GridItem>

            <GridItem>
              <FormControl display="flex" alignItems="center" mt={{ base: 0, md: 8 }}>
                <FormLabel htmlFor="custom-id" mb="0">
                  Custom Rep ID
                </FormLabel>
                <Switch
                  id="custom-id"
                  isChecked={customId}
                  onChange={(e) => setCustomId(e.target.checked)}
                />
              </FormControl>
              <VStack align="stretch" mt={2} spacing={2}>
                {!customId ? (
                  <Text fontSize="sm" color="gray.600">
                    Will be: <Badge>{`CG-REP-${suggestedSuffix}`}</Badge>
                  </Text>
                ) : (
                  <FormControl>
                    <FormLabel>
                      Rep ID Suffix (after <code>CG-REP-</code>)
                    </FormLabel>
                    <Input
                      placeholder={suggestedSuffix}
                      value={suffix}
                      onChange={(e) => setSuffix(e.target.value)}
                      _hover={{ transform: "scale(1)" }}
                    />
                    <FormHelperText>Letters only; uppercase enforced.</FormHelperText>
                  </FormControl>
                )}
              </VStack>
            </GridItem>
          </Grid>

          <HStack mt={6}>
            <Button
              colorScheme="teal"
              onClick={onCreateRep}
              isDisabled={!canSubmitCreate || loading}
              isLoading={loading}
              leftIcon={<CheckCircleIcon />}
              _hover={{ transform: "scale(1)" }}
            >
              Create Rep
            </Button>
          </HStack>
        </Box>

        {/* ASSIGN REP TO FACILITY */}
        <Box borderWidth="1px" borderRadius="lg" p={5} bg="gray.50">
          <HStack justify="space-between" mb={4} wrap="wrap">
            <Heading size="md">Assign Rep to Facility</Heading>
            <Tag colorScheme="gray">
              <TagLabel>Facilities</TagLabel>
            </Tag>
          </HStack>

          <Stack spacing={4}>
            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel>Select Rep</FormLabel>
                  <Select
                    value={selectedRepId}
                    onChange={(e) => setSelectedRepId(e.target.value)}
                    _hover={{ transform: "scale(1)" }}
                  >
                    {reps.map((r) => {
                      const name = [r.firstName, r.lastName]
                        .filter(Boolean)
                        .join(" ");
                      const code = r.repCode || r.repId;
                      return (
                        <option key={r.repId} value={r.repId}>
                          {name || r.email} — {code}
                        </option>
                      );
                    })}
                  </Select>
                  <FormHelperText>Pulled from /reps.</FormHelperText>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Search Facility</FormLabel>
                  <Input
                    placeholder="Name, city, NPI, or Facility ID…"
                    value={facilityQuery}
                    onChange={(e) => onFacilityInput(e.target.value)}
                    _hover={{ transform: "scale(1)" }}
                  />
                  <FormHelperText>Type to search (min 2 chars).</FormHelperText>
                </FormControl>
              </GridItem>
            </Grid>

            {searchingFacilities ? (
              <HStack color="gray.600">
                <Spinner size="sm" />
                <Text>Searching…</Text>
              </HStack>
            ) : (
              <Box>
                {facilityResults.length > 0 ? (
                  <Stack spacing={2}>
                    {facilityResults.map((f) => {
                      const label =
                        f.accountName ||
                        f.facilityName ||
                        f.name ||
                        f.practiceName ||
                        f.account_id ||
                        f.facilityId ||
                        "Facility";
                      const id = f.account_id || f.facilityId || f.id;
                      return (
                        <HStack
                          key={id}
                          justify="space-between"
                          borderWidth="1px"
                          borderRadius="md"
                          p={2}
                        >
                          <Box>
                            <Text fontWeight="semibold">{label}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {id}
                            </Text>
                            {f.city || f.state ? (
                              <Text fontSize="xs" color="gray.500">
                                {[f.city, f.state].filter(Boolean).join(", ")}
                              </Text>
                            ) : null}
                          </Box>
                          <Button
                            size="sm"
                            variant={
                              selectedFacilityId === id ? "solid" : "outline"
                            }
                            colorScheme="blue"
                            onClick={() => setSelectedFacilityId(id)}
                            _hover={{ transform: "scale(1)" }}
                          >
                            {selectedFacilityId === id ? "Selected" : "Select"}
                          </Button>
                        </HStack>
                      );
                    })}
                  </Stack>
                ) : facilityQuery.length >= 2 ? (
                  <Text color="gray.600">No matches.</Text>
                ) : null}
              </Box>
            )}

            <Divider />

            <HStack>
              <Button
                colorScheme="purple"
                onClick={onAssign}
                isDisabled={!selectedRepId || !selectedFacilityId || loading}
                isLoading={loading}
                _hover={{ transform: "scale(1)" }}
              >
                Assign Rep to Facility
              </Button>
            </HStack>
          </Stack>
        </Box>
      </Stack>

      {/* Override Confirmation Modal */}
      <Modal isOpen={isOverrideOpen} onClose={onOverrideClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Facility Already Assigned</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <Text>
                This facility already has a sales rep assigned.
              </Text>
              {overrideContext.existingSalesRepId ? (
                <Text fontSize="sm" color="gray.600">
                  Current Rep:{" "}
                  <Badge colorScheme="gray">
                    {overrideContext.existingSalesRepId}
                  </Badge>
                </Text>
              ) : null}
              <Text>
                Do you want to <b>replace</b> the existing assignment with{" "}
                <Badge colorScheme="purple">{overrideContext.repId}</Badge>?
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button
                variant="outline"
                onClick={onOverrideClose}
                _hover={{ transform: "scale(1)" }}
              >
                Cancel
              </Button>
              <Tooltip
                label="This will overwrite the facility's current salesRepId"
                hasArrow
              >
                <Button
                  colorScheme="red"
                  onClick={onConfirmOverride}
                  isLoading={overrideSubmitting}
                  _hover={{ transform: "scale(1)" }}
                >
                  Override Assignment
                </Button>
              </Tooltip>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RepManageTab;
